import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { trpc } from "../../lib/trpc";

interface Props {
  open: boolean;
  woId: string | null;
  woCode?: string;
  itemLabel?: string;
  plannedQty?: number;
  producedQty?: number;
  onClose: () => void;
  onLinked?: (res: { linked: number; completed: boolean; woCode: string }) => void;
}

interface StockBatchRow {
  id: string;
  code: string;
  qty: number;
  uom: string;
  lotCode: string | null;
  lotStatus: string | null;
  lotQtyOnHand: number | null;
  locationCode: string;
  locationName: string;
  producedAt: string;
}

// Inventory.tsx ile aynı lot durumu etiketleri.
const LOT_STATUS_TR: Record<string, string> = {
  released: "Serbest",
  pending_qc: "QC Bekliyor",
  on_hold: "Blokeli",
  expired: "SKT Geçti",
  rejected: "Reddedildi",
  consumed: "Tükendi",
};

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
}
function fmtDate(d: string): string {
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}

/**
 * Stoktan kullan — daha önce stoğa üretilmiş (sipariş-siz + iş-emri-siz) partileri çoklu seçip
 * iş emri üretimine toplu bağlar. Üretim TEKRAR yapılmaz; mevcut lotlar kullanılır.
 */
export function StockBatchLinkDrawer({ open, woId, woCode, itemLabel, plannedQty, producedQty, onClose, onLinked }: Props) {
  const qc = useQueryClient();
  const [sel, setSel] = useState<Set<string>>(new Set());
  /**
   * Kısmi kullanım: parti başına ayrılacak miktar (boş = TAMAMI).
   * Metin olarak tutulur — sayıya çevirmek yazarken "0,5" gibi ara değerleri bozar.
   */
  const [kismi, setKismi] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const headRef = useRef<HTMLInputElement>(null);

  const listQ = trpc.productionBatch.unallocatedStockBatches.useQuery(
    { woId: woId ?? "" },
    { enabled: open && !!woId, staleTime: 15_000 },
  );
  const rows = (listQ.data ?? []) as StockBatchRow[];

  const linkMut = trpc.productionBatch.linkToWorkOrder.useMutation();

  useEffect(() => {
    if (open) { setSel(new Set()); setKismi({}); setErr(null); linkMut.reset(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, woId]);

  const selectedRows = useMemo(() => rows.filter((r) => sel.has(r.id)), [rows, sel]);
  const selCount = selectedRows.length;
  /** Bir satırın KULLANILACAK miktarı: kısmi girilmişse o, değilse partinin tamamı. */
  const kullanilan = (r: StockBatchRow): number => {
    const t = (kismi[r.id] ?? "").trim().replace(",", ".");
    if (!t) return r.qty;
    const n = Number(t);
    return Number.isFinite(n) && n > 0 ? Math.min(n, r.qty) : r.qty;
  };
  const selQty = selectedRows.reduce((s, r) => s + kullanilan(r), 0);
  const uom = rows[0]?.uom ?? "";
  const remaining = plannedQty != null && producedQty != null ? Math.max(0, plannedQty - producedQty) : null;
  const allChecked = rows.length > 0 && selCount === rows.length;
  const someChecked = selCount > 0 && !allChecked;

  // Header onay kutusunun "kısmi" (indeterminate) durumunu DOM üzerinden ayarla (React prop'u yok).
  useEffect(() => { if (headRef.current) headRef.current.indeterminate = someChecked; }, [someChecked, selCount, rows.length]);

  function toggle(id: string) {
    setSel((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  function toggleAll() {
    // allChecked (başlık kutusunun gösterdiği değer) üzerinden karar ver — bayat id'ler sel.size'ı
    // şişirip "seç" yerine "temizle" tetiklemesin.
    setSel(() => (allChecked ? new Set() : new Set(rows.map((r) => r.id))));
  }

  async function submit() {
    setErr(null);
    if (!woId) return;
    // Yalnız hâlâ listede görünen (geçerli) seçimleri gönder — bayat id sunucuda CONFLICT'e yol açmasın.
    if (selectedRows.length === 0) return setErr("En az bir parti seçin.");
    // Geçersiz kısmi girdiyi SESSİZCE tamamına çevirmek yerine kullanıcıya söyle.
    const hatali = selectedRows.find((r) => {
      const t = (kismi[r.id] ?? "").trim().replace(",", ".");
      if (!t) return false;
      const n = Number(t);
      return !Number.isFinite(n) || n <= 0 || n > r.qty + 1e-9;
    });
    if (hatali) return setErr(`${hatali.code}: miktar 0 ile ${fmtQty(hatali.qty)} arasında olmalı.`);
    const batches = selectedRows.map((r) => {
      const q = kullanilan(r);
      // Tamamı seçildiyse qty GÖNDERİLMEZ → sunucu bölme yapmaz (gereksiz lot türetmeyelim).
      return q >= r.qty - 1e-9 ? { id: r.id } : { id: r.id, qty: q };
    });
    try {
      const res = await linkMut.mutateAsync({ woId, batches });
      await qc.invalidateQueries();
      onLinked?.({ linked: res.linked, completed: res.completed, woCode: res.woCode });
      onClose();
    } catch {
      /* linkMut.error banner */
    }
  }

  const mutErr = linkMut.error?.message ?? null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title="Stoktan Kullan — Parti Bağla"
      subtitle={woCode ? `İş Emri: ${woCode}${itemLabel ? ` · ${itemLabel}` : ""}` : undefined}
      footer={
        <>
          <div style={{ marginRight: "auto", fontSize: 12.5, color: "var(--neutral-600)" }}>
            <b className="mono" style={{ color: "var(--neutral-900)" }}>{selCount}</b> parti seçili
            {selCount > 0 ? (
              <> · <b className="mono" style={{ color: "var(--neutral-900)" }}>{fmtQty(selQty)}{uom ? ` ${uom}` : ""}</b></>
            ) : null}
            {remaining != null ? (
              <span style={{ color: "var(--neutral-500)" }}> · Kalan {fmtQty(remaining)}{uom ? ` ${uom}` : ""}</span>
            ) : null}
          </div>
          <button type="button" className="btn-md ghost" onClick={onClose} disabled={linkMut.isPending}>İptal</button>
          <button type="button" className="btn-md primary" onClick={submit} disabled={linkMut.isPending || selCount === 0}>
            {linkMut.isPending ? "Bağlanıyor…" : "Bağla"}
          </button>
        </>
      }
    >
      {err || mutErr ? <div className="form-banner-err" role="alert">{err ?? mutErr}</div> : null}
      <p className="help" style={{ marginBottom: 12 }}>
        Bu ürün için daha önce <b>stoğa üretilmiş</b> (siparişe/iş emrine bağlı olmayan) partiler. Seçtiklerin bu iş emrinin üretimine sayılır; <b>yeni üretim yapılmaz</b>, mevcut lotlar kullanılır.
      </p>
      <p className="help" style={{ marginBottom: 12 }}>
        <b>Kısmi kullanım:</b> "Kullanılacak" boş bırakılırsa partinin tamamı bağlanır. Daha az girerseniz parti <b>ve lotu bölünür</b>: ayrılan miktar <b>türetilmiş lot koduna</b> (ör. <span className="mono">LOT-…/1</span>) geçer, kalan özgün lotta serbest kalır. Ayrılan kısma <b>yeni lot etiketi basılmalıdır</b>.
      </p>

      {listQ.isLoading ? (
        <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
      ) : listQ.isError ? (
        <div className="empty-state">
          <p className="ttl" style={{ color: "var(--danger)" }}>Veri yüklenemedi</p>
          <p className="sub">{listQ.error?.message ?? "Stok üretimleri getirilemedi."}</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p className="ttl">Bağlanabilecek stok üretimi yok</p>
          <p className="sub">Bu ürün için sipariş/iş emri bağı olmayan parti bulunamadı. Önce "Stoğa Üretim" ile üretebilirsiniz.</p>
        </div>
      ) : (
        <div className="dt2-wrap">
          <div className="dt2-scroll">
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input ref={headRef} type="checkbox" className="cb" checked={allChecked} onChange={toggleAll} aria-label="Tümünü seç" />
                  </th>
                  <th>Parti</th>
                  <th className="num" style={{ width: 104 }}>Miktar</th>
                  <th className="num" style={{ width: 128 }} title="Boş bırakılırsa partinin tamamı kullanılır. Daha az girilirse parti ve LOTU bölünür; ayrılan kısma türetilmiş lot kodu verilir.">Kullanılacak</th>
                  <th style={{ width: 156 }}>Çıktı Lotu</th>
                  <th className="num" style={{ width: 100 }}>Lot Stok</th>
                  <th style={{ width: 150 }}>Lokasyon</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => toggle(r.id)} style={{ cursor: "pointer" }}>
                    <td style={{ paddingLeft: 14 }} onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="cb" checked={sel.has(r.id)} onChange={() => toggle(r.id)} aria-label={`Seç ${r.code}`} />
                    </td>
                    <td><span className="cell-id">{r.code}</span><span className="sec" style={{ fontSize: 10.5 }}>{fmtDate(r.producedAt)}</span></td>
                    <td className="cell-num">{fmtQty(r.qty)}<span className="unit">{r.uom}</span></td>
                    <td className="num" onClick={(e) => e.stopPropagation()}>
                      <input
                        className="form-input" type="number" step="any" min={0} max={r.qty}
                        inputMode="decimal" placeholder="Tamamı" aria-label={`${r.code} için kullanılacak miktar`}
                        disabled={!sel.has(r.id)}
                        value={kismi[r.id] ?? ""}
                        onChange={(e) => setKismi((p) => ({ ...p, [r.id]: e.target.value }))}
                        style={{ width: "100%", textAlign: "right" }}
                      />
                    </td>
                    <td>
                      {r.lotCode ? (
                        <>
                          <span className="cell-id">{r.lotCode}</span>
                          {r.lotStatus ? <span className="sec" style={{ fontSize: 10.5 }}>{LOT_STATUS_TR[r.lotStatus] ?? r.lotStatus}</span> : null}
                        </>
                      ) : (
                        <span style={{ color: "var(--neutral-400)" }}>—</span>
                      )}
                    </td>
                    <td className="cell-num">
                      {r.lotQtyOnHand != null ? (
                        <span style={{ color: r.lotQtyOnHand > 0 ? "var(--neutral-900)" : "var(--warning-text)" }}>{fmtQty(r.lotQtyOnHand)}</span>
                      ) : "—"}
                    </td>
                    <td>{r.locationName}<span className="sec mono" style={{ fontSize: 10.5 }}>{r.locationCode}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Drawer>
  );
}
